(() => {
// Native contact host. Activate only after matching Atelier parser and receipt verification.
// Serializer contract remains ns.voucher_request.v1, 16 keys, terms notice r3.
const BODY_PREFIX = 'CONSIGLIERI_NOVEMBER_SELECTION_V1\n';
const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const INVALID_CONTROL = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/;
const SINGLE_LINE_CONTROL = /[\r\n\t]/;

class RequestValidationError extends Error {
  constructor(field, code) {
    super(code);
    this.name = 'RequestValidationError';
    this.field = field;
    this.code = code;
  }
}

function text(value, field, min, max, multiline = false) {
  if (typeof value !== 'string') throw new RequestValidationError(field, 'invalid_type');
  const normalized = value.trim();
  // JS length and HTML maxlength both count UTF-16 code units.
  if (normalized.length < min || normalized.length > max) {
    throw new RequestValidationError(field, 'invalid_length');
  }
  if (INVALID_CONTROL.test(normalized) || (!multiline && SINGLE_LINE_CONTROL.test(normalized))) {
    throw new RequestValidationError(field, 'invalid_characters');
  }
  return normalized;
}

function serializeRequest(input, context) {
  // Activation, notices and source context are supplied by the separately reviewed host.
  // These checks are UI safeguards, never authentication or commercial authority.
  if (context.noticesVerified !== true) throw new RequestValidationError('form', 'notices_not_ready');
  if (!['public', 'house_client'].includes(context.audienceRequested)) {
    throw new RequestValidationError('form', 'invalid_audience');
  }
  // This template's canonical URL comes from Liquid. The private URL must never
  // appear in a shared asset; the exact audience/source enum is enforced in Intake.
  const sourceUrl = text(context.sourceUrl, 'form', 1, 300);
  if (!/^https:\/\/consiglieri\.ro\/pages\/[a-z0-9-]+$/.test(sourceUrl)) {
    throw new RequestValidationError('form', 'invalid_source');
  }
  const name = text(input.name, 'name', 1, 150);
  const email = text(input.email, 'email', 1, 254);
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email)) {
    throw new RequestValidationError('email', 'invalid_email');
  }
  const phone = text(input.phone, 'phone', 3, 40);
  if (!/^\+?[0-9 ()-]+$/.test(phone)) throw new RequestValidationError('phone', 'invalid_phone');
  const offerRef = text(input.offerRef, 'offer', 1, 20);
  const offer = context.catalog.find(item => item.offer_ref === offerRef);
  if (!offer || !['CM', 'RTW'].includes(offer.kind)) {
    throw new RequestValidationError('offer', 'unknown_offer');
  }
  const size = text(input.sizePreference, 'size', 0, 160);
  if (offer.kind === 'CM' && size !== '') throw new RequestValidationError('size', 'cm_size_must_be_empty');
  const details = text(input.details, 'details', 0, 1500, true);
  if (input.privacyAck !== true) throw new RequestValidationError('privacy', 'ack_required');
  if (input.termsNoticeAck !== true) throw new RequestValidationError('terms', 'ack_required');
  if (typeof context.testLabel !== 'boolean') throw new RequestValidationError('form', 'invalid_test_label');
  const requestId = context.requestId;
  if (typeof requestId !== 'string' || !UUID_V4.test(requestId)) {
    throw new RequestValidationError('form', 'invalid_reference');
  }
  const payload = {
    schema_version: 'ns.voucher_request.v1',
    service_line: 'november_selection',
    campaign_id: 'ns_2026_11',
    request_id: requestId,
    audience_requested: context.audienceRequested,
    offer_kind: offer.kind,
    offer_ref: offerRef,
    size_preference: size,
    phone,
    details,
    source_url: sourceUrl,
    privacy_notice_version: 'ns-2026-11-02',
    privacy_ack: true,
    ns_test: context.testLabel,
    terms_notice_version: 'ns-terms-2026-11-03-r3',
    terms_notice_ack: true
  };
  const json = JSON.stringify(payload);
  if (json.length > 10000) throw new RequestValidationError('form', 'body_too_long');
  return {
    requestId,
    contactFields: {
      'contact[name]': name,
      'contact[email]': email,
      'contact[body]': BODY_PREFIX + json
    }
  };
}

// Pure state decisions; the host owns native Shopify submission and session-only storage.
const PENDING_TTL_MS = 30 * 60 * 1000;
const CAMPAIGN_START = 1793916000000;
const CAMPAIGN_END = 1794175200000;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function validPending(value, source, now) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    && Object.keys(value).sort().join(',') === 'createdAt,requestId,source'
    && typeof value.requestId === 'string' && UUID.test(value.requestId)
    && value.source === source && Number.isFinite(value.createdAt)
    && now >= value.createdAt && now - value.createdAt <= PENDING_TTL_MS;
}

function restoredState({pending, source, now, nativeSuccess, nativeErrors}) {
  // A success query or Liquid flag alone never produces a received message.
  if (!validPending(pending, source, now)) return nativeErrors ? 'validation_error' : 'ready';
  if (nativeErrors) return 'validation_error';
  return nativeSuccess ? 'received' : 'unknown_result';
}

function campaignOpen(now) {
  return Number.isFinite(now) && now >= CAMPAIGN_START && now < CAMPAIGN_END;
}

const NS_PINNED_CATALOG = [{"offer_ref":"L01","kind":"CM"},{"offer_ref":"P01","kind":"CM"},{"offer_ref":"S01","kind":"CM"},{"offer_ref":"E01","kind":"CM"},{"offer_ref":"E02","kind":"CM"},{"offer_ref":"O01","kind":"CM"},{"offer_ref":"RTW-16058476495179","kind":"RTW"},{"offer_ref":"RTW-16058477478219","kind":"RTW"},{"offer_ref":"RTW-16058477805899","kind":"RTW"},{"offer_ref":"RTW-16058482655563","kind":"RTW"},{"offer_ref":"RTW-16058489962827","kind":"RTW"},{"offer_ref":"RTW-16058495467851","kind":"RTW"},{"offer_ref":"RTW-16058495631691","kind":"RTW"},{"offer_ref":"RTW-16058495828299","kind":"RTW"},{"offer_ref":"RTW-16058496352587","kind":"RTW"},{"offer_ref":"RTW-16058496876875","kind":"RTW"},{"offer_ref":"RTW-16058497040715","kind":"RTW"},{"offer_ref":"RTW-16058497368395","kind":"RTW"},{"offer_ref":"RTW-16058497794379","kind":"RTW"},{"offer_ref":"RTW-16058497925451","kind":"RTW"},{"offer_ref":"RTW-16058500350283","kind":"RTW"},{"offer_ref":"RTW-16058507067723","kind":"RTW"},{"offer_ref":"RTW-16058511851851","kind":"RTW"},{"offer_ref":"RTW-16058512179531","kind":"RTW"},{"offer_ref":"RTW-16058512572747","kind":"RTW"},{"offer_ref":"RTW-16058512802123","kind":"RTW"},{"offer_ref":"RTW-16058512998731","kind":"RTW"},{"offer_ref":"RTW-16058513260875","kind":"RTW"},{"offer_ref":"RTW-16058513555787","kind":"RTW"},{"offer_ref":"RTW-16058514309451","kind":"RTW"},{"offer_ref":"RTW-16058516242763","kind":"RTW"},{"offer_ref":"RTW-16058534003019","kind":"RTW"},{"offer_ref":"RTW-16058534199627","kind":"RTW"},{"offer_ref":"RTW-16058536132939","kind":"RTW"},{"offer_ref":"RTW-16059959968075","kind":"RTW"},{"offer_ref":"RTW-16059960131915","kind":"RTW"},{"offer_ref":"RTW-16059960197451","kind":"RTW"},{"offer_ref":"RTW-16059960295755","kind":"RTW"},{"offer_ref":"RTW-16059960525131","kind":"RTW"},{"offer_ref":"RTW-16059960656203","kind":"RTW"},{"offer_ref":"RTW-16059960885579","kind":"RTW"},{"offer_ref":"RTW-16059960951115","kind":"RTW"},{"offer_ref":"RTW-16059961016651","kind":"RTW"},{"offer_ref":"RTW-16059961082187","kind":"RTW"},{"offer_ref":"RTW-16076163580235","kind":"RTW"},{"offer_ref":"RTW-16076163842379","kind":"RTW"},{"offer_ref":"RTW-16076165185867","kind":"RTW"},{"offer_ref":"RTW-16076165316939","kind":"RTW"},{"offer_ref":"RTW-16076165742923","kind":"RTW"},{"offer_ref":"RTW-16076165841227","kind":"RTW"},{"offer_ref":"RTW-16076166005067","kind":"RTW"},{"offer_ref":"RTW-16076166201675","kind":"RTW"},{"offer_ref":"RTW-16076166332747","kind":"RTW"},{"offer_ref":"RTW-16076166562123","kind":"RTW"},{"offer_ref":"RTW-16076166725963","kind":"RTW"},{"offer_ref":"RTW-16076166922571","kind":"RTW"},{"offer_ref":"RTW-16076167119179","kind":"RTW"},{"offer_ref":"RTW-16076167446859","kind":"RTW"},{"offer_ref":"RTW-16076167577931","kind":"RTW"}];
// Concatenated after the serializer and pure state module by build-candidate.py.
// Native HTML form submission only: no fetch, programmatic submit, analytics or retry.
(() => {
  let disposeCurrent;
  const initialize = () => {
  const form = document.getElementById('cs-ns-contact');
  if (!form || form.dataset.nsBound) return;
  if (disposeCurrent) disposeCurrent();
  form.dataset.nsBound = 'true';
  const lifecycle = new AbortController();
  const listenerOptions = {signal:lifecycle.signal};
  const panel = form.querySelector('[data-ns-request-host]');
  const source = panel.dataset.source;
  const sessionKey = 'cs.ns.pending.v1';
  const fields = form.querySelector('[data-ns-fields]');
  const button = form.querySelector('[type="submit"]');
  const status = panel.querySelector('[data-ns-status]');
  const reference = panel.querySelector('[data-ns-reference]');
  const body = form.elements['contact[body]'];
  const control = key => form.querySelector('[data-ns-field="' + key + '"]');
  let submitting = false;
  let timer;
  const root = document.getElementById('cs-ns');
  const clock = window.CSNovemberClock?.get(root);
  if (!clock) return;
  const now = clock.now;
  let unsubscribe = () => {}, windowMessage = false;
  disposeCurrent = () => { clearTimeout(timer); unsubscribe(); lifecycle.abort(); };
  let pending = null;
  const catalog = NS_PINNED_CATALOG;
  const verifiedOptions = Array.from(control('offer').options).filter(o => o.value).map(o => o.value);
  const catalogDrift = verifiedOptions.length !== catalog.length || new Set(verifiedOptions).size !== catalog.length ||
      verifiedOptions.some(ref => !catalog.some(item => item.offer_ref === ref));
  const message = (text, id='') => {
    status.textContent = text;
    status.hidden = false;
    reference.textContent = id;
    reference.hidden = !id;
  };
  const closedMessage = () => now() >= CAMPAIGN_END
    ? 'Perioada de solicitări s-a încheiat.'
    : 'Solicitările sunt disponibile în perioada 6–8 noiembrie 2026.';
  const revealResult = () => {
    const wrapper = form.closest('[data-ns-when-open]');
    if (wrapper) { wrapper.removeAttribute('data-ns-when-open'); wrapper.hidden = false; }
  };
  const unknown = () => {
    revealResult();
    button.disabled = true;
    message('Nu putem confirma trimiterea. Contactați echipa și menționați referința solicitării înainte de a trimite din nou.', pending?.requestId || '');
  };
  try { pending = JSON.parse(sessionStorage.getItem(sessionKey) || 'null'); } catch (_) { /* No PII fallback. */ }
  const state = restoredState({pending, source, now:now(), nativeSuccess:panel.dataset.nativeSuccess==='true', nativeErrors:panel.dataset.nativeErrors==='true'});
  if (state === 'received') {
    revealResult();
    fields.hidden = true;
    message('Solicitarea a fost transmisă. Un consultant vă va contacta pentru confirmarea eligibilității și disponibilității. Voucherul se emite după confirmare și încasare.',pending.requestId);
    try { sessionStorage.removeItem(sessionKey); } catch (_) { /* Reference stays visible. */ }
    return;
  }
  if (state === 'unknown_result') { unknown(); return; }
  // Never hide a completed/uncertain request just because the live catalogue changed.
  if (catalogDrift) {
    message('Selecția este în curs de actualizare. Contactați echipa Consiglieri pentru solicitarea dumneavoastră.'); return;
  }
  if (!validPending(pending,source,now())) pending=null;
  const syncWindow = () => {
    const current = now();
    const allowed = panel.dataset.ready === 'true'
      && (panel.dataset.test === 'true' || campaignOpen(current));
    panel.dataset.canSubmit = String(allowed);
    fields.disabled = !allowed;
    button.disabled = !allowed || submitting;
    if (!allowed && !submitting) {
      message(closedMessage()); windowMessage = true;
    } else if (windowMessage && !submitting) {
      status.hidden = true; windowMessage = false;
    }
  };
  const updateSize = () => {
    const isRtw = control('offer').value.startsWith('RTW-');
    control('size').disabled = !isRtw;
    form.querySelector('.cn-size-field').hidden = !isRtw;
    if (!isRtw) control('size').value='';
  };
  control('offer').addEventListener('change',updateSize,listenerOptions);
  // Existing card selection changes the select without firing change.
  document.querySelectorAll('#cs-ns [data-offer]').forEach(b=>b.addEventListener('click',updateSize,listenerOptions));
  updateSize();
  unsubscribe = clock.subscribe(syncWindow);
  form.addEventListener('input',e=>{
    e.target.removeAttribute('aria-invalid');
    if (typeof e.target.setCustomValidity === 'function') e.target.setCustomValidity('');
  },listenerOptions);
  form.addEventListener('submit',event=>{
    if (submitting) { event.preventDefault(); return; }
    syncWindow();
    if (panel.dataset.canSubmit !== 'true') {
      event.preventDefault(); button.disabled=true;
      message(closedMessage()); return;
    }
    if (!form.reportValidity()) { event.preventDefault(); return; }
    try {
      const result=serializeRequest({
        name:control('name').value,email:control('email').value,phone:control('phone').value,
        offerRef:control('offer').value,sizePreference:control('size').value,details:control('details').value,
        privacyAck:control('privacy').checked,termsNoticeAck:control('terms').checked
      },{
        noticesVerified:panel.dataset.noticesVerified==='true',audienceRequested:panel.dataset.audience,sourceUrl:source,
        catalog,requestId:pending?.requestId || crypto.randomUUID(),testLabel:panel.dataset.test==='true'
      });
      control('name').value=result.contactFields['contact[name]'];
      control('email').value=result.contactFields['contact[email]'];
      body.value=result.contactFields['contact[body]'];
      // Shared scripts may not add booking/lead contact fields to this form.
      const contactKeys=Array.from(new FormData(form).keys()).filter(k=>k.startsWith('contact['));
      if(contactKeys.length!==3 || new Set(contactKeys).size!==3 || contactKeys.some(k=>!Object.hasOwn(result.contactFields,k))) {
        throw new RequestValidationError('form','unexpected_transport_fields');
      }
      pending={requestId:result.requestId,source,createdAt:now()};
      sessionStorage.setItem(sessionKey,JSON.stringify(pending));
      if (!validPending(JSON.parse(sessionStorage.getItem(sessionKey)),source,now())) throw new Error('session');
      submitting=true;button.disabled=true;
      message('Se transmite solicitarea. Dacă Shopify cere o verificare, urmați pașii afișați.',pending.requestId);
      // Keep Shopify's own form action, challenge listeners and default navigation.
      timer=setTimeout(unknown,60000);
    } catch(error) {
      event.preventDefault();
      if(error instanceof RequestValidationError && control(error.field)) {
        const target=control(error.field);
        target.setAttribute('aria-invalid','true');
        target.setCustomValidity('Verificați acest câmp și limitele afișate.');
        target.reportValidity();target.focus();
        message('Verificați câmpul indicat înainte de trimitere.');
      } else {
        message('Formularul nu poate fi pregătit pentru trimitere. Contactați echipa pentru solicitare.');
      }
    }
  },listenerOptions);
  window.addEventListener('pagehide',()=>clearTimeout(timer),listenerOptions);
  window.addEventListener('pageshow',event=>{ if(event.persisted && submitting) unknown(); else syncWindow(); },listenerOptions);
  };
  initialize();
  document.addEventListener('shopify:section:load',initialize);
})();

})();
